import { createContext, useContext, useEffect, useSyncExternalStore } from "react";
import {
  DEFAULT_THEME,
  THEME_ATTRIBUTE,
  THEME_STORAGE_KEY as STORAGE_KEY,
  type Theme,
} from "@/lib/theme";

function readStoredTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

const themeStore = {
  listeners: new Set<() => void>(),
  subscribe(listener: () => void) {
    themeStore.listeners.add(listener);
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    window.addEventListener("storage", listener);
    media.addEventListener("change", listener);
    return () => {
      themeStore.listeners.delete(listener);
      window.removeEventListener("storage", listener);
      media.removeEventListener("change", listener);
    };
  },
  emit() {
    for (const listener of themeStore.listeners) listener();
  },
  set(theme: Theme) {
    localStorage.setItem(STORAGE_KEY, theme);
    themeStore.emit();
  },
};

const ThemeContext = createContext<{
  theme: Theme;
  toggle: () => void;
} | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const theme = useSyncExternalStore(themeStore.subscribe, readStoredTheme, () => DEFAULT_THEME);

  useEffect(() => {
    document.documentElement.setAttribute(THEME_ATTRIBUTE, theme);
  }, [theme]);

  function toggle() {
    themeStore.set(theme === "light" ? "dark" : "light");
  }

  return <ThemeContext.Provider value={{ theme, toggle }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
